/**
 * 搜索面板组件
 * 提供数据搜索和过滤功能
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { debounce } from '../utils/format';
import { useSearch, useKeys, useSearchSuggestions } from '../hooks/useApi';
import Button from './ui/Button';
import Input from './ui/Input';
import Card from './ui/Card';
import type { SearchParams } from '../types';

interface SearchPanelProps {
  onSearchResults: (results: any) => void;
  className?: string;
}

const SearchPanel: React.FC<SearchPanelProps> = ({
  onSearchResults,
  className
}) => {
  const [searchParams, setSearchParams] = useState<SearchParams>({
    key: '',
    value: '',
    limit: 20,
    offset: 0
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState<'key' | 'value' | null>(null);

  const { searchResult, loading, error, search } = useSearch();
  const { keys } = useKeys();
  const { suggestions, getSuggestions } = useSearchSuggestions();

  // 搜索防抖
  const debouncedSearch = useCallback(
    debounce((params: SearchParams) => {
      if (params.key || params.value) {
        search(params);
      }
    }, 500),
    [search]
  );

  // 获取搜索建议防抖
  const debouncedGetSuggestions = useCallback(
    debounce((query: string) => {
      if (query.length >= 2) {
        getSuggestions(query);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }, 300),
    [getSuggestions]
  );

  // 处理搜索参数变化
  const handleParamChange = (field: keyof SearchParams, value: string | number) => {
    const newParams = { ...searchParams, [field]: value };
    setSearchParams(newParams);
    
    if (field === 'key' || field === 'value') {
      debouncedSearch(newParams);
      if (typeof value === 'string' && value) {
        debouncedGetSuggestions(value);
      }
    }
  };

  // 处理搜索提交
  const handleSearch = () => {
    search(searchParams);
  };

  // 清空搜索
  const handleClear = () => {
    const clearedParams = { key: '', value: '', limit: 20, offset: 0 };
    setSearchParams(clearedParams);
    setShowSuggestions(false);
    // 执行空搜索来显示所有数据，而不是清空结果
    search(clearedParams);
  };

  // 选择建议项
  const handleSuggestionSelect = (suggestion: string, field: 'key' | 'value') => {
    handleParamChange(field, suggestion);
    setShowSuggestions(false);
    setActiveField(null);
  };

  // 处理输入框焦点
  const handleInputFocus = (field: 'key' | 'value') => {
    setActiveField(field);
    const value = searchParams[field];
    if (value && value.length >= 2) {
      debouncedGetSuggestions(value);
    }
  };

  // 处理输入框失焦
  const handleInputBlur = () => {
    // 延迟隐藏建议，允许点击建议项
    setTimeout(() => {
      setShowSuggestions(false);
      setActiveField(null);
    }, 200);
  };

  // 更新搜索结果
  useEffect(() => {
    if (searchResult) {
      onSearchResults(searchResult);
    }
  }, [searchResult, onSearchResults]);

  // 页面加载时自动执行一次搜索显示所有数据
  useEffect(() => {
    // 初始加载时执行空搜索来显示所有数据
    search({ key: '', value: '', limit: 20, offset: 0 });
  }, []); // 只在组件挂载时执行一次

  const filteredSuggestions = activeField === 'key' ? (suggestions.keys || []) : (suggestions.values || []);

  return (
    <Card className={clsx('sticky top-4 z-10', className)}>
      <div className="space-y-4">
        {/* 基础搜索 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Input
              placeholder="搜索键名..."
              value={searchParams.key || ''}
              onChange={(e) => handleParamChange('key', e.target.value)}
              onFocus={() => handleInputFocus('key')}
              onBlur={handleInputBlur}
              icon={<Search />}
              iconPosition="left"
            />
            
            {/* 键名建议 */}
            <AnimatePresence>
              {showSuggestions && activeField === 'key' && filteredSuggestions && filteredSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50"
                >
                  {filteredSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none first:rounded-t-lg last:rounded-b-lg"
                      onClick={() => handleSuggestionSelect(suggestion, 'key')}
                    >
                      <span className="text-sm text-gray-900">{suggestion}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="relative">
            <Input
              placeholder="搜索值..."
              value={searchParams.value || ''}
              onChange={(e) => handleParamChange('value', e.target.value)}
              onFocus={() => handleInputFocus('value')}
              onBlur={handleInputBlur}
              icon={<Search />}
              iconPosition="left"
            />
            
            {/* 值建议 */}
            <AnimatePresence>
              {showSuggestions && activeField === 'value' && filteredSuggestions && filteredSuggestions.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto z-50"
                >
                  {filteredSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none first:rounded-t-lg last:rounded-b-lg"
                      onClick={() => handleSuggestionSelect(suggestion, 'value')}
                    >
                      <span className="text-sm text-gray-900">{suggestion}</span>
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* 高级选项切换 */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <Filter className="w-4 h-4" />
            <span>高级选项</span>
            <ChevronDown className={clsx(
              'w-4 h-4 transition-transform',
              showAdvanced && 'rotate-180'
            )} />
          </button>

          <div className="flex items-center space-x-2">
            {(searchParams.key || searchParams.value) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                icon={<X />}
              >
                清空
              </Button>
            )}
            <Button
              onClick={handleSearch}
              loading={loading}
              disabled={!searchParams.key && !searchParams.value}
              icon={<Search />}
            >
              搜索
            </Button>
          </div>
        </div>

        {/* 高级选项 */}
        <AnimatePresence>
          {showAdvanced && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-t border-gray-200 pt-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="每页显示"
                  value={searchParams.limit?.toString() || '20'}
                  onChange={(e) => handleParamChange('limit', parseInt(e.target.value) || 20)}
                  min="1"
                  max="100"
                />
                <Input
                  type="number"
                  label="偏移量"
                  value={searchParams.offset?.toString() || '0'}
                  onChange={(e) => handleParamChange('offset', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 错误提示 */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <p className="text-sm text-red-600">{error}</p>
          </motion.div>
        )}

        {/* 快速键名选择 */}
        {keys && keys.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">常用键名</h4>
            <div className="flex flex-wrap gap-2">
              {keys.slice(0, 10).map((key) => (
                <button
                  key={key}
                  onClick={() => handleParamChange('key', key)}
                  className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full transition-colors"
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SearchPanel;